import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { customersApi, type CustomerSearchResult } from '../../api/customers.api.js';
import { withdrawalsApi } from '../../api/withdrawals.api.js';

/**
 * Typing in a transfer that has already left a bank we operate by hand.
 *
 * The outgoing twin of recording a deposit. The difference that matters is
 * which way the money went, and it changes what this is: a deposit lands in the
 * review queue because somebody still has to decide whether the customer may
 * have it, while this money is gone. Nothing is being decided here. The bank
 * sent it, and the ledger is being told.
 *
 * So it debits the customer immediately and files the transfer as already sent.
 * The bank's own reference is required for the same reason as on the incoming
 * side: two members of staff working the same statement is an ordinary event,
 * and nothing else in the system saw this payment.
 *
 * No commission is charged. Whatever the bank took, it took; a fee added here
 * would be a charge with no transaction behind it.
 */

/** Accounts at a bank with nothing behind it but our own operators. */
const MANUAL_MECHANICS = new Set(['MANUAL_SHARED']);

/** "12.34" / "12,34" / "12" → minor units. Refuses anything else. */
function toCents(input: string): bigint | null {
  const cleaned = input.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [whole, frac = ''] = cleaned.split('.');
  const cents = BigInt(whole ?? '0') * 100n + BigInt(frac.padEnd(2, '0'));
  return cents > 0n ? cents : null;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface RecordManualTransferDialogProps {
  open: boolean;
  onClose: () => void;
  onRecorded: (message: string) => void;
}

export function RecordManualTransferDialog({
  open,
  onClose,
  onRecorded,
}: RecordManualTransferDialogProps): JSX.Element {
  const [customerQuery, setCustomerQuery] = useState('');
  const [customer, setCustomer] = useState<CustomerSearchResult | null>(null);
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [beneficiaryAccount, setBeneficiaryAccount] = useState('');
  const [beneficiaryBank, setBeneficiaryBank] = useState('');
  const [reference, setReference] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [valueDate, setValueDate] = useState(today);
  const [failure, setFailure] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['manual-transfer-customers', customerQuery],
    queryFn: () => customersApi.search(customerQuery, 20),
    enabled: open && customerQuery.trim().length >= 2,
  });

  const chosen = useQuery({
    queryKey: ['manual-transfer-accounts', customer?.customerId ?? ''],
    queryFn: () => customersApi.getCustomer(customer?.customerId ?? ''),
    enabled: open && customer !== null,
  });

  const manualAccounts = (chosen.data?.accounts ?? []).filter((a) =>
    MANUAL_MECHANICS.has(a.provider),
  );
  const account = manualAccounts.find((a) => a.accountId === accountId) ?? null;

  const reset = (): void => {
    setCustomerQuery('');
    setCustomer(null);
    setAccountId('');
    setAmount('');
    setBeneficiaryName('');
    setBeneficiaryAccount('');
    setBeneficiaryBank('');
    setReference('');
    setBankReference('');
    setValueDate(today());
    setFailure(null);
  };

  const dismiss = (): void => {
    reset();
    onClose();
  };

  const record = useMutation({
    mutationFn: (input: Parameters<typeof withdrawalsApi.recordManual>[0]) =>
      withdrawalsApi.recordManual(input),
    onSuccess: () => {
      const message = 'Recorded. The account has been debited to match the bank.';
      reset();
      onRecorded(message);
    },
    onError: (err: unknown) => {
      setFailure(err instanceof Error ? err.message : 'The transfer could not be recorded.');
    },
  });

  const cents = toCents(amount);
  const incomplete =
    customer === null ||
    account === null ||
    cents === null ||
    beneficiaryName.trim().length < 2 ||
    beneficiaryAccount.trim().length < 4 ||
    bankReference.trim().length < 3 ||
    valueDate.trim() === '';

  const submit = (): void => {
    if (customer === null || account === null || cents === null) return;
    setFailure(null);
    record.mutate({
      provider: account.provider,
      customerId: customer.customerId,
      accountId: account.accountId,
      amountCents: cents.toString(),
      currency: account.currency,
      beneficiaryName: beneficiaryName.trim(),
      beneficiaryAccount: beneficiaryAccount.trim(),
      ...(beneficiaryBank.trim() !== '' ? { beneficiaryBank: beneficiaryBank.trim() } : {}),
      ...(reference.trim() !== '' ? { reference: reference.trim() } : {}),
      bankReference: bankReference.trim(),
      valueDate,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={record.isPending ? undefined : dismiss}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Record a transfer that already left</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">
            This debits the customer straight away. Use it only for money the bank has already
            sent — it records what happened rather than asking anyone to approve it.
          </Alert>

          <TextField
            label="Find the customer"
            size="small"
            value={customerQuery}
            onChange={(e) => {
              setCustomerQuery(e.target.value);
              setCustomer(null);
              setAccountId('');
            }}
            helperText="Search by name or email, at least two characters."
          />

          {customer === null && customerQuery.trim().length >= 2 ? (
            <TextField
              select
              size="small"
              label="Customer"
              value=""
              onChange={(e) => {
                const found = (options.data ?? []).find((c) => c.customerId === e.target.value);
                setCustomer(found ?? null);
              }}
              helperText={options.isPending ? 'Searching…' : `${(options.data ?? []).length} found`}
            >
              {(options.data ?? []).map((c) => (
                <MenuItem key={c.customerId} value={c.customerId}>
                  {c.maskedName} · {c.maskedEmail}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          {customer !== null ? (
            <Typography variant="body2" color="text.secondary">
              {customer.maskedName} · {customer.maskedEmail}
            </Typography>
          ) : null}

          {customer !== null ? (
            <TextField
              select
              size="small"
              label="Account the money left"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              helperText={
                manualAccounts.length === 0
                  ? 'This customer holds no account at a bank we operate by hand.'
                  : 'Only manual banks are listed. An integrated provider reports its own transfers.'
              }
            >
              {manualAccounts.map((a) => (
                <MenuItem key={a.accountId} value={a.accountId}>
                  {a.currency} · {a.iban !== '' ? a.iban : 'no IBAN yet'} · {a.provider}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label={`Amount sent${account !== null ? ` (${account.currency})` : ''}`}
              size="small"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              error={amount.trim() !== '' && cents === null}
              helperText={
                amount.trim() !== '' && cents === null ? 'Enter an amount like 1250.00' : ' '
              }
            />
            <TextField
              label="Value date"
              type="date"
              size="small"
              value={valueDate}
              onChange={(e) => setValueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <TextField
            label="Who it went to"
            size="small"
            value={beneficiaryName}
            onChange={(e) => setBeneficiaryName(e.target.value)}
          />
          <TextField
            label="Account it went to"
            size="small"
            value={beneficiaryAccount}
            onChange={(e) => setBeneficiaryAccount(e.target.value)}
            helperText="As printed on the bank's screen."
          />
          <TextField
            label="Their bank (optional)"
            size="small"
            value={beneficiaryBank}
            onChange={(e) => setBeneficiaryBank(e.target.value)}
          />
          <TextField
            label="Payment reference (optional)"
            size="small"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            helperText="What the customer asked to be written on the transfer."
          />
          <TextField
            label="The bank's own reference"
            size="small"
            value={bankReference}
            onChange={(e) => setBankReference(e.target.value)}
            helperText="Required. It is what stops the same transfer being entered twice."
          />

          {failure !== null ? <Alert severity="error">{failure}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={dismiss} disabled={record.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={incomplete || record.isPending}
        >
          {record.isPending ? 'Recording…' : 'Record and debit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
